<template>
    <div class="video-player-page">
        <div class="player-header" @click="goBack">
            <van-icon name="arrow-left" />
            <span>返回视频列表</span>
        </div>

        <div v-if="detail" class="player-main-card">
            <h2>{{ detail.title }}</h2>
            <div class="player-meta">
                <span>{{ detail.classify_name || detail.category || '未分类' }}</span>
                <span>{{ detail.duration || '未知时长' }}</span>
            </div>
            <video
                v-if="playUrl"
                ref="videoRef"
                :src="mediaSrc"
                controls
                autoplay
                preload="metadata"
                @play="onVideoPlay"
                @pause="onVideoPause"
                @ended="onVideoEnded"
                @error="onVideoError"
            ></video>
            <div v-else class="empty-player">暂无可播放地址</div>
            <p>{{ detail.description || '暂无简介' }}</p>
        </div>

        <div v-if="topBannerAds.length" class="banner-stack">
            <a
                v-for="ad in topBannerAds"
                :key="`top-${ad.id}`"
                :href="ad.url || 'javascript:void(0)'"
                target="_blank"
                rel="noopener noreferrer"
                class="banner-item"
            >
                <img :src="ad.pic" alt="" />
            </a>
        </div>

        <div class="recommend-wrap">
            <div class="recommend-title">随机推荐</div>
            <div v-if="recommendList.length" class="recommend-list">
                <div v-for="item in recommendList" :key="item.id" class="recommend-item" @click="goPlay(item.id)">
                    <img v-if="item.cover" :src="item.cover" alt="" />
                    <div v-else class="cover-placeholder">封面</div>
                    <div class="recommend-info">
                        <h3>{{ item.title }}</h3>
                        <div class="meta">
                            <span>{{ item.classify_name || item.category || '未分类' }}</span>
                            <span>{{ item.duration || '未知时长' }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="empty-tip">暂无推荐视频</div>

            <van-pagination
                v-if="recommendTotal > recommendPageSize"
                v-model="recommendPageNum"
                :total-items="recommendTotal"
                :items-per-page="recommendPageSize"
                force-ellipses
                @change="onRecommendPageChange"
            />
        </div>

        <div v-if="bottomBannerAds.length" class="banner-stack bottom-stack">
            <a
                v-for="ad in bottomBannerAds"
                :key="`bottom-${ad.id}`"
                :href="ad.url || 'javascript:void(0)'"
                target="_blank"
                rel="noopener noreferrer"
                class="banner-item"
            >
                <img :src="ad.pic" alt="" />
            </a>
        </div>
    </div>
</template>

<script>
import Hls from 'hls.js'

export default {
    name: 'VideoPlayerPage',
    data() {
        return {
            detail: null,
            topBannerAds: [],
            bottomBannerAds: [],
            recommendList: [],
            recommendPageNum: 1,
            recommendPageSize: 20,
            recommendTotal: 0,
            hasReportedPlayStart: false,
            reportedSeconds: 0,
            hls: null,
            mediaSrc: '',
            currentSource: ''
        }
    },
    computed: {
        videoId() {
            return Number(this.$route.params.id || 0)
        },
        rawPlayUrl() {
            const rawUrl = this.getRawPlayableUrl()
            return this.unwrapProxyTargetUrl(rawUrl)
        },
        playUrl() {
            const cleanUrl = this.rawPlayUrl
            if (!cleanUrl) return ''
            if (!/^https?:\/\//i.test(cleanUrl)) return cleanUrl
            const base = String((this.$http && this.$http.defaults && this.$http.defaults.baseURL) || '').replace(/\/api\/?$/i, '')
            if (!base) return cleanUrl
            return `${base}/api/video_proxy?url=${encodeURIComponent(cleanUrl)}`
        },
        sourceCandidates() {
            return Array.from(new Set([
                String(this.playUrl || '').trim(),
                String(this.rawPlayUrl || '').trim()
            ].filter(Boolean)))
        }
    },
    watch: {
        '$route.params.id': {
            immediate: true,
            handler() {
                this.recommendPageNum = 1
                this.resetPlayStatState()
                this.loadAll()
            }
        },
        playUrl: {
            immediate: true,
            async handler() {
                await this.setupPlayerSource()
            }
        }
    },
    beforeDestroy() {
        this.destroyHls()
        this.reportPlayedDuration()
    },
    methods: {
        getRawPlayableUrl() {
            const detail = this.detail || {}
            const candidates = [
                detail.url_raw,
                detail.raw_url,
                detail.source_url,
                detail.play_source_url,
                detail.url,
                detail.play_url,
                detail.playUrl,
                detail.vod_url,
                detail.vod_play_url,
                detail.vod_jumpurl
            ]

            for (const candidate of candidates) {
                const value = this.unwrapProxyTargetUrl(candidate)
                if (value) return value
            }

            return ''
        },
        unwrapProxyTargetUrl(inputUrl = '') {
            let current = String(inputUrl || '').trim()
            for (let i = 0; i < 5; i++) {
                if (!/\/api\/video_proxy\?/i.test(current)) break
                try {
                    const parsed = new URL(current)
                    const nested = parsed.searchParams.get('url')
                    if (!nested) break
                    current = decodeURIComponent(String(nested || '').trim())
                } catch (error) {
                    const match = current.match(/[?&]url=([^&]+)/i)
                    if (!match || !match[1]) break
                    current = decodeURIComponent(String(match[1]).trim())
                }
            }
            return current
        },
        destroyHls() {
            if (this.hls) {
                this.hls.destroy()
                this.hls = null
            }
        },
        getFallbackSource(failedSource = '') {
            const source = String(failedSource || this.currentSource || this.mediaSrc || '').trim()
            const list = this.sourceCandidates
            const currentIndex = list.indexOf(source)
            if (currentIndex >= 0 && currentIndex < list.length - 1) {
                return list[currentIndex + 1]
            }
            if (!source && list.length > 0) {
                return list[0]
            }
            return ''
        },
        async handlePlaybackFailure(failedSource = '') {
            const fallbackSource = this.getFallbackSource(failedSource)
            if (!fallbackSource || fallbackSource === failedSource) return
            await this.setupPlayerSource(fallbackSource)
        },
        isM3u8Url(url = '') {
            return /\.m3u8(\?|$)/i.test(String(url || ''))
        },
        async setupPlayerSource(preferredSource = '') {
            this.destroyHls()
            this.mediaSrc = ''
            this.currentSource = ''

            const source = String(preferredSource || this.sourceCandidates[0] || '').trim()
            await this.$nextTick()

            const video = this.$refs.videoRef
            if (!video || !source) return
            this.currentSource = source

            if (this.isM3u8Url(source)) {
                const canNativePlay = !!video.canPlayType('application/vnd.apple.mpegurl')
                if (!canNativePlay && Hls.isSupported()) {
                    const hls = new Hls({
                        enableWorker: true
                    })
                    this.hls = hls
                    hls.loadSource(source)
                    hls.attachMedia(video)
                    hls.on(Hls.Events.ERROR, (_event, data) => {
                        if (data && data.fatal) {
                            this.destroyHls()
                            this.handlePlaybackFailure(source)
                        }
                    })
                    return
                }
            }

            this.mediaSrc = source
        },
        async onVideoError() {
            await this.handlePlaybackFailure(this.currentSource || this.mediaSrc)
        },
        resetPlayStatState() {
            this.hasReportedPlayStart = false
            this.reportedSeconds = 0
        },
        goBack() {
            this.reportPlayedDuration()
            this.$router.push('/video')
        },
        goPlay(id) {
            if (!id) return
            this.reportPlayedDuration()
            this.$router.push(`/video/play/${id}`)
        },
        async reportVideoPlayStat({ playCountInc = 0, playSeconds = 0 } = {}) {
            if (!this.videoId) return
            try {
                await this.$http.post('/video_play_report', {
                    video_id: this.videoId,
                    play_count_inc: playCountInc,
                    play_seconds: playSeconds
                })
            } catch (error) {
                // 统计上报失败不影响播放流程
            }
        },
        async onVideoPlay() {
            if (!this.hasReportedPlayStart) {
                this.hasReportedPlayStart = true
                await this.reportVideoPlayStat({ playCountInc: 1, playSeconds: 0 })
            }
        },
        async reportPlayedDuration() {
            const video = this.$refs.videoRef
            if (!video) return
            const currentSec = Math.max(0, Math.floor(Number(video.currentTime || 0)))
            const delta = currentSec - this.reportedSeconds
            if (delta <= 0) return
            this.reportedSeconds = currentSec
            await this.reportVideoPlayStat({ playCountInc: 0, playSeconds: delta })
        },
        async onVideoPause() {
            await this.reportPlayedDuration()
        },
        async onVideoEnded() {
            await this.reportPlayedDuration()
        },
        async loadDetail() {
            if (!this.videoId) {
                this.detail = null
                return
            }
            const { data: res } = await this.$http.get('/video_detail', { params: { id: this.videoId } })
            this.detail = res.data || null
        },
        async loadAds() {
            const [{ data: topRes }, { data: bottomRes }] = await Promise.all([
                this.$http.get('/ad_slot_list', { params: { ad_type: 'video_play_top' } }),
                this.$http.get('/ad_slot_list', { params: { ad_type: 'video_play_bottom' } })
            ])
            this.topBannerAds = topRes.data || []
            this.bottomBannerAds = bottomRes.data || []
        },
        async loadRecommend() {
            const params = {
                page_num: this.recommendPageNum,
                page_size: this.recommendPageSize,
                exclude_id: this.videoId
            }
            if (this.detail && this.detail.classify_id) {
                params.classify_id = this.detail.classify_id
            }
            const { data: res } = await this.$http.get('/video_random_list', { params })
            this.recommendList = res.data || []
            this.recommendTotal = Number(res.paging?.total || 0)
        },
        async onRecommendPageChange(page) {
            this.recommendPageNum = page
            await this.loadRecommend()
            window.scrollTo({ top: 0, behavior: 'smooth' })
        },
        async loadAll() {
            try {
                await this.loadDetail()
                await Promise.all([this.loadAds(), this.loadRecommend()])
            } catch (error) {
                this.topBannerAds = []
                this.bottomBannerAds = []
                this.recommendList = []
                this.recommendTotal = 0
            }
        }
    }
}
</script>

<style lang="less" scoped>
.video-player-page {
    padding: 0;
    padding-bottom: .5rem;
}
.player-header {
    display: flex;
    align-items: center;
    gap: .4rem;
    height: 2.8rem;
    color: #22d3ee;
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: .25rem;
}
.player-main-card {
    background: linear-gradient(145deg, rgba(15, 23, 42, 0.95), rgba(9, 14, 34, 0.92));
    border-radius: 1rem;
    border: 1px solid rgba(34, 211, 238, .16);
    box-shadow: var(--a-shadow);
    padding: .9rem;
    h2 {
        font-size: 1.18rem;
        margin-bottom: .5rem;
        color: #f8fafc;
    }
    p {
        margin-top: .6rem;
        color: #cbd5e1;
        font-size: .9rem;
        line-height: 1.5;
    }
    video {
        width: 100%;
        border-radius: .75rem;
        background: #000;
        margin-top: .5rem;
    }
}
.player-meta {
    display: flex;
    justify-content: space-between;
    color: #8b5cf6;
    font-size: .85rem;
    font-weight: 700;
}
.empty-player {
    margin-top: .5rem;
    line-height: 8rem;
    text-align: center;
    border-radius: .75rem;
    background: #edf6ff;
    color: #60718f;
}
.banner-stack {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: .6rem;
}
.banner-item {
    display: block;
    width: 100%;
    img {
        width: 100%;
        height: 4.2rem;
        object-fit: contain;
        background: #f7f8fa;
        border-radius: .75rem;
        box-shadow: 0 8px 14px rgba(17, 56, 124, .16);
    }
}
.recommend-wrap {
    margin-top: 1rem;
    background: var(--a-surface);
    border-radius: 1rem;
    border: 1px solid rgba(17, 109, 255, .16);
    box-shadow: var(--a-shadow);
    padding: .8rem;
}
.recommend-title {
    font-size: 1.1rem;
    font-weight: 800;
    color: #1a3260;
    margin-bottom: .7rem;
}
.recommend-list {
    display: flex;
    flex-direction: column;
    gap: .7rem;
}
.recommend-item {
    display: flex;
    gap: .7rem;
    img, .cover-placeholder {
        width: 6.5rem;
        height: 4.3rem;
        border-radius: .7rem;
        object-fit: cover;
        background: #edf6ff;
        color: #60718f;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }
}
.recommend-info {
    flex: 1;
    h3 {
        font-size: 1rem;
        margin-bottom: .45rem;
        line-height: 1.4;
        color: #1f3152;
    }
    .meta {
        color: #116dff;
        display: flex;
        justify-content: space-between;
        font-size: .8rem;
        font-weight: 700;
    }
}
.empty-tip {
    text-align: center;
    color: #60718f;
    padding: 1rem 0;
}
.van-pagination {
    margin-top: .9rem;
}
.bottom-stack {
    margin-bottom: .5rem;
}
</style>
